import { getLandingExperiment, trackLandingAccountCreated } from "../analytics/landingExperiment";
import { initializeApp } from "firebase/app";
import { requestVerificationEmail } from "./emailVerification";
import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  documentId,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  increment,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  orderBy,
  startAfter,
  arrayUnion,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
// import firestore so i can store the files based off user accounts
import {
  deleteObject,
  getDownloadURL,
  getMetadata,
  getStorage,
  listAll,
  ref,
  uploadString,
} from "firebase/storage";

import { GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult } from "firebase/auth";
import { OAuthProvider } from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { mobileAuth } from "./mobileAuth";

import { getPublicSVGsWithAlgolia, searchPublicSVGsWithAlgolia, searchPublicSVGsByCreator } from "../algolia/algolia";
import { METADATA_COLLECTION, FILES_SUBCOLLECTION, SVG_FOLDER, SVG_GENERATED_FOLDER, REFERENCE_ARTWORK_FOLDER } from "../../config/firebaseVariant";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Identify the active product at runtime as well as build time. The hostname
// fallback protects production signups if a hosting build is ever created
// without REACT_APP_VARIANT, which would otherwise mislabel EmbroideryTrace
// accounts as SvgTrace and route analytics/onboarding to the wrong product.
const getCurrentAppId = () => {
  const hostname = typeof window !== "undefined"
    ? window.location.hostname.toLowerCase()
    : "";
  return process.env.REACT_APP_VARIANT === "embroiderytrace" ||
    hostname.includes("embroiderytrace")
    ? "embroiderytrace"
    : "svgtrace";
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export default app;

// On native Capacitor the default indexedDB persistence can hang inside WKWebView.
// Use browserLocalPersistence (localStorage) as primary on native; keep indexedDB as
// fallback on web for larger storage.
export const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: browserLocalPersistence })
  : getAuth(app);

// Eagerly initialize native social login so the SDK is ready before the user
// taps any sign-in button (avoids first-tap failures on iOS).
if (Capacitor.isNativePlatform()) {
  mobileAuth.initialize().catch(err =>
    console.warn('⚠️ Eager SocialLogin init failed (will retry on login):', err.message)
  );
}

export const handleSignUp = async (email, password, utmData = null) => {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );

  trackLandingAccountCreated();

  // Update the user profile with the provided name
  // await updateProfile(auth.currentUser, {
  //     displayName: name,
  // });

  const db = getFirestore();
  const createdAt = new Date();
  
  // Prepare user document data
  const signupApp = getCurrentAppId();
  const userData = {
    uid: userCredential.user.uid,
    email,
    createdAt,
    signupApp,
    lastActiveApp: signupApp,
    apps: [signupApp],
    ...(getLandingExperiment() ? { landingExperiment: getLandingExperiment() } : {}),
  };

  // Add UTM tracking data if provided and has actual data
  if (utmData && (
    (utmData.session && Object.keys(utmData.session).length > 0) ||
    (utmData.firstTouch && Object.keys(utmData.firstTouch).length > 0) ||
    (utmData.lastTouch && Object.keys(utmData.lastTouch).length > 0)
  )) {
    userData.utmData = utmData;
    console.log('✅ Adding UTM data to user document:', utmData);
  } else {
    console.log('⚠️ No UTM data provided for signup (or empty):', utmData);
  }

  // Use setDoc with the UID so the Firestore trigger can find it by uid
  const userDocRef = doc(db, "users", userCredential.user.uid);
  await setDoc(userDocRef, userData, { merge: true });

  // Email delivery must not prevent saving an account that already exists.
  await requestVerificationEmail(userCredential.user, "signup");

  // Handle successful sign-up here
  console.log("User created:", userCredential.user);
};
export const handleSignIn = async (email, password) => {
  try {
    console.log("🔍 FIREBASE AUTH: Attempting sign-in with email:", email);
    
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    
    console.log("✅ FIREBASE AUTH: User signed in successfully:", {
      email: userCredential.user.email,
      uid: userCredential.user.uid,
      emailVerified: userCredential.user.emailVerified
    });
    
    return userCredential;
  } catch (error) {
    console.error("🚨 FIREBASE AUTH ERROR:", {
      code: error.code,
      message: error.message,
      email: email
    });
    throw error;
  }
};

export const signOutUser = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await mobileAuth.logout('google');
      await mobileAuth.logout('apple');
    } catch (error) {
      console.error('Failed to logout from native social providers:', error);
    }
  }
  await signOut(auth);
};

export const resetPassworEmail = async (email) =>
  await sendPasswordResetEmail(auth, email);

// Function to get user display name by UID - returns Reddit-style anonymous identifier
export const getUserDisplayName = async (uid) => {
  try {
    // Reddit-style username generation
    const adjectives = [
      'Curious', 'Wise', 'Brave', 'Swift', 'Clever', 'Bold', 'Gentle', 'Fierce', 'Noble', 'Witty',
      'Calm', 'Bright', 'Mystic', 'Silent', 'Golden', 'Silver', 'Crimson', 'Azure', 'Emerald', 'Violet',
      'Ancient', 'Modern', 'Cosmic', 'Electric', 'Frozen', 'Burning', 'Dancing', 'Flying', 'Hidden', 'Shining'
    ];
    
    const animals = [
      'Panda', 'Koala', 'Eagle', 'Wolf', 'Tiger', 'Dolphin', 'Owl', 'Fox', 'Bear', 'Lion',
      'Hawk', 'Whale', 'Deer', 'Rabbit', 'Turtle', 'Penguin', 'Falcon', 'Swan', 'Raven', 'Lynx',
      'Otter', 'Seal', 'Shark', 'Octopus', 'Phoenix', 'Dragon', 'Unicorn', 'Griffin', 'Sphinx', 'Pegasus'
    ];

    // Use UID to generate consistent but pseudo-random indices
    const uidHash = uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    const adjIndex = uidHash % adjectives.length;
    const animalIndex = (uidHash * 7) % animals.length; // Multiply by prime to get different index
    const number = (uidHash % 999) + 1; // Number between 1-999
    
    return `${adjectives[adjIndex]}${animals[animalIndex]}${number}`;
  } catch (error) {
    console.error("Error creating anonymous display name:", error);
    return `Artist${uid.substring(0, 8)}`;
  }
};

/**
 * Get a public file by its ID (svgMetadata document ID)
 * This is used for share links - no authentication required
 */
export const getPublicFileById = async (fileId) => {
  try {
    console.log(`🔗 Fetching public file by ID: ${fileId}`);
    
    // Fetch the document from svgMetadata collection
    const docRef = doc(db, METADATA_COLLECTION, fileId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log(`❌ File not found: ${fileId}`);
      return null;
    }
    
    const data = docSnap.data();
    
    // Check if file is public
    if (!data.isPublic) {
      console.log(`🔒 File is private: ${fileId}`);
      return null;
    }
    
    // Get the URL (prefer publicUrl if available)
    let url = data.publicUrl;
    
    if (!url && data.filePath) {
      // Fall back to generating download URL
      try {
        const storageRef = ref(storage, data.filePath);
        url = await getDownloadURL(storageRef);
      } catch (error) {
        console.error(`Error getting download URL for ${fileId}:`, error);
        return null;
      }
    }
    
    console.log(`✅ Public file fetched successfully: ${fileId}`);
    
    return {
      id: docSnap.id,
      ...data,
      url,
      publicUrl: data.publicUrl,
    };
  } catch (error) {
    console.error(`Error fetching public file ${fileId}:`, error);
    throw error;
  }
};

/**
 * Get the svgMetadata document ID by file path
 * Used when we have a storage ref but need the correct Firestore document ID
 */
export const getSvgMetadataIdByPath = async (storageRef) => {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    const filePath = storageRef._location?.path_ || storageRef;
    const svgMetadataRef = collection(db, METADATA_COLLECTION);
    const q = query(
      svgMetadataRef,
      where("ownerUid", "==", user.uid),
      where("filePath", "==", filePath)
    );

    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].id;
    }
    return null;
  } catch (error) {
    console.error("Error getting svgMetadata ID by path:", error);
    return null;
  }
};

/**
 * Get file metadata (seoTitle, prompt, etc.) from Firestore
 * Used to fetch SEO data for share links when not available in cache
 */
export const getFileMetadata = async (fileId) => {
  try {
    const docRef = doc(db, METADATA_COLLECTION, fileId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      seoTitle: data.seoTitle || '',
      seoDescription: data.seoDescription || '',
      prompt: data.prompt || '',
      searchKeywords: data.searchKeywords || [],
    };
  } catch (error) {
    console.error(`Error fetching file metadata ${fileId}:`, error);
    return null;
  }
};

/**
 * Generate a URL-friendly slug from a title
 */
const slugify = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .substring(0, 50); // Limit length
};

/**
 * Generate a share link for a file
 * Returns the shareable URL with SEO-friendly slug
 * Format: /community/{title-slug}-{fileId}
 */
export const generateShareLink = (fileId, title = '') => {
  // Always use the production svgtrace.com URL for share links
  const baseUrl = 'https://svgtrace.com';
  const titleSlug = slugify(title);
  
  // If we have a title, create a nice slug like "snoopy-winter-svg-abc123"
  // Otherwise just use the fileId
  const slug = titleSlug ? `${titleSlug}-${fileId}` : fileId;
  
  return `${baseUrl}/community/${slug}`;
};

/**
 * Extract the fileId from a share URL slug
 * Handles both formats: "title-slug-{fileId}" and just "{fileId}"
 */
export const extractFileIdFromSlug = (slug) => {
  if (!slug) return null;
  
  // The fileId is always at the end after the last hyphen
  // Firestore document IDs are typically 20 characters
  // Check if the slug ends with what looks like a Firestore ID
  const parts = slug.split('-');
  
  // If the last part looks like a Firestore ID (alphanumeric, ~20 chars)
  const lastPart = parts[parts.length - 1];
  if (lastPart && /^[a-zA-Z0-9]{15,25}$/.test(lastPart)) {
    return lastPart;
  }
  
  // Otherwise, the entire slug might be the fileId
  return slug;
};

export const verifyEmail = async () => {
  return requestVerificationEmail(auth.currentUser);
};

const functions = getFunctions(app);
export const createPortalLinkFunc = httpsCallable(
  functions,
  "createProductPortalLink"
);

// Intercom Identity Verification — returns { userHash } for the Messenger boot
export const getIntercomUserHashFunc = httpsCallable(
  functions,
  "getIntercomUserHash"
);

// Initialize Storage
const storage = getStorage(app);
export { storage };

export const uploadSVGToStorage = async (data, folder) => {
  const user = auth.currentUser;
  const userId = user.uid;
  const storageRef = ref(
    storage,
    `${userId}/${folder}/${new Date().getTime()}.svg`
  );
  const res = await uploadString(storageRef, data, "data_url");
  const sr = ref(storage, res.metadata.fullPath);
  const url = await getDownloadURL(sr);
  return { res, url };
};

export const uploadReferenceArtworkToStorage = async (dataUrl) => {
  const user = auth.currentUser;
  if (!user) throw new Error("No authenticated user found");
  if (!/^data:image\//i.test(dataUrl || "")) {
    throw new Error("Reference artwork must be an image data URL");
  }

  const mimeType = dataUrl.slice(5, dataUrl.indexOf(";")) || "image/png";
  const extensionByMime = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
  };
  const extension = extensionByMime[mimeType.toLowerCase()] || "png";
  const storageRef = ref(
    storage,
    `${user.uid}/${REFERENCE_ARTWORK_FOLDER}/${new Date().getTime()}.${extension}`
  );
  const res = await uploadString(storageRef, dataUrl, "data_url", { contentType: mimeType });
  const url = await getDownloadURL(ref(storage, res.metadata.fullPath));
  return { res, url, mimeType };
};

export const writeSVGToDatabase = async (filePath, prompt = "", imageSEO, isPublic = false, fileType = "generated", workflow = "vector", extraFields = {}) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user found");

    const userId = user.uid;

    // Handle cases where imageSEO might be undefined or missing properties
    const seoTitle = imageSEO?.title ? imageSEO.title.replace(/"/g, "") : "";
    const seoDescription = imageSEO?.description ? imageSEO.description.replace(/"/g, "") : "";
    
    // Write to primary metadata collection (authoritative source)
    const docRef = await addDoc(collection(db, METADATA_COLLECTION), {
      filePath,
      prompt, // Optional prompt parameter
      createdAt: serverTimestamp(), // Automatically generate the timestamp
      isPublic: isPublic, // Use the passed parameter
      ownerUid: userId, // Owner's UID from the authenticated user
      shareLink: "", // Assume you will handle this separately or update later
      downloadCount: 0,
      seoTitle: seoTitle,
      seoDescription: seoDescription,
      type: fileType, // Also add type field for Algolia compatibility
      workflow: workflow, // "vector" or "stitch"
      ...extraFields, // e.g. forkedFrom provenance for community copies
    });

    // Also write to accountData collection for fast rendering (cache layer)
    console.log(`💾 DUAL WRITE: Adding new ${fileType} file to cache...`);
    console.log(`   File ID: ${docRef.id}`);
    console.log(`   User: ${userId}`);
    console.log(`   Prompt: "${prompt || 'No prompt'}"`);
    console.log(`   isPublic: ${isPublic}`);
    console.log(`   fileType: ${fileType}`);
    
    try {
      await addDoc(collection(db, "accountData", userId, FILES_SUBCOLLECTION), {
        id: docRef.id, // Reference to metadata document
        filepath: filePath,
        timeCreated: serverTimestamp(),
        type: fileType, // Use the actual fileType parameter (generated, uploaded, or sharpened)
        isPublic: isPublic, // Use the passed parameter
        prompt: prompt, // Cached for display
        svgMetadataId: docRef.id, // Explicit reference for sync
        seoTitle: seoTitle,
        seoDescription: seoDescription,
        fileType: fileType, // Also add to cache
        workflow: workflow, // "vector" or "stitch"
        ...extraFields,
      });
      console.log(`✅ DUAL WRITE SUCCESS: New ${fileType} file cached successfully`);
    } catch (accountDataError) {
      console.error("❌ DUAL WRITE ERROR: Failed to write to accountData collection:", accountDataError);
      // Don't throw - accountData is cache layer, svgMetadata is source of truth
    }

    console.log("SVG written to both collections", docRef.id);
    return docRef;
  } catch (error) {
    console.error("Error writing SVG to Database", error);
    throw error; // Rethrow or handle as needed
  }
};

/**
 * Restore the separately stored source artwork for an SVG after a reload.
 * The editor's Redux/session state is only a convenience; Firestore remains
 * the durable source of truth and the Storage URL is refreshed on demand.
 */
export const loadReferenceArtworkForFile = async ({
  filePath,
  svgMetadataId,
  referenceArtwork: knownArtwork,
} = {}) => {
  const user = auth.currentUser;
  if (!user) return knownArtwork || null;

  let artwork = knownArtwork || null;

  if (!artwork && svgMetadataId) {
    try {
      const metadataSnapshot = await getDoc(doc(db, METADATA_COLLECTION, svgMetadataId));
      const metadata = metadataSnapshot.exists() ? metadataSnapshot.data() : null;
      if (metadata?.ownerUid === user.uid) artwork = metadata.referenceArtwork || null;
    } catch (error) {
      console.warn("Could not restore reference artwork from SVG metadata:", error.message);
    }
  }

  if (!artwork && filePath) {
    try {
      const filesRef = collection(db, "accountData", user.uid, FILES_SUBCOLLECTION);
      const snapshot = await getDocs(query(filesRef, where("filepath", "==", filePath), limit(1)));
      artwork = snapshot.docs[0]?.data()?.referenceArtwork || null;
    } catch (error) {
      console.warn("Could not restore reference artwork from the file cache:", error.message);
    }
  }

  if (artwork?.path) {
    try {
      artwork = {
        ...artwork,
        url: await getDownloadURL(ref(storage, artwork.path)),
      };
    } catch (error) {
      console.warn("Could not resolve the reference artwork URL:", error.message);
    }
  }

  return artwork;
};

// Updates the user's SVG in Firebase Storage (same object the svgMetadata doc references).
// Caller may embed extra payload in the SVG (e.g. <metadata data-svgtrace-stitch-state> for
// Stitch mode rails + per-layer settings — see useSaveSvg embedStitchState / extractStitchState).
// Account-level files remain one Storage path + svgMetadata (or accountData cache) row per design;
// optional future: dual-write garment-sidecar JSON or DST paths if you need stitch artifacts
// indexed separately from the vector file.
export const updateSVGInStorage = async (storageRef, data) => {
  const res = await uploadString(storageRef, data, "data_url");
  const sr = ref(storage, res.metadata.fullPath);
  const url = await getDownloadURL(sr);
  return { url, filePath: res.metadata.fullPath };
};

// ── Stitch State (Firestore) ───────────────────────────────────────────────
// Stores per-file stitch settings + rails in accountData/{userId}/stitchStates/{fileId}
// so the SVG file itself stays clean and unmodified.

// File ids arrive in two shapes depending on where the file was opened from:
// the Converter uses the raw Storage filename ("1234.svg") while the dashboard
// list uses the catalog doc id with the extension stripped ("1234" — the
// imageUploaded cloud function strips it). Before this normalizer existed the
// two paths read/wrote DIFFERENT stitchStates docs, so satin work saved right
// after converting looked lost after a refresh. Always key by the
// extensionless id; loads fall back to (and migrate) the legacy ".svg" doc.
export const normalizeStitchFileId = (fileId) =>
  typeof fileId === "string" ? fileId.replace(/\.svg$/i, "") : fileId;

export const saveStitchStateToFirestore = async (userId, fileId, state) => {
  if (!userId || !fileId || !state) return;
  fileId = normalizeStitchFileId(fileId);
  // Firestore rejects undefined values — strip them by round-tripping through JSON.
  // aiAnalysis MUST go through the same sanitize: the AI result often contains
  // undefined optional fields, and a single undefined makes the whole setDoc throw
  // (so aiAnalysis was never persisted and the editor re-ran analysis on reopen).
  const sanitized = JSON.parse(JSON.stringify({
    version: state.version ?? 1,
    layerStitchSettings: state.layerStitchSettings || {},
    layerRails: state.layerRails || {},
    layerCutMarks: state.layerCutMarks || {},
    lastGeneratedFile: state.lastGeneratedFile || null,
    // Only write aiAnalysis when present — merge keeps any existing value on partial saves.
    ...(state.aiAnalysis ? { aiAnalysis: state.aiAnalysis } : {}),
    // Hoop + target size. Conditional for the same reason as aiAnalysis: a
    // partial save must not blank a stored value. The per-layer settings are
    // tuned for a physical size, so they only mean anything alongside it.
    ...(state.hoopSettings ? { hoopSettings: state.hoopSettings } : {}),
    // Keep AI-retraced reference images private alongside the stitch state.
    // Partial saves preserve the reference, and base64 images stay out of Firestore.
    ...(state.referenceArtwork?.path ? {
      referenceArtwork: {
        path: state.referenceArtwork.path,
        mimeType: state.referenceArtwork.mimeType || null,
        name: state.referenceArtwork.name || "Reference artwork",
        sourceWidth: state.referenceArtwork.sourceWidth || null,
        sourceHeight: state.referenceArtwork.sourceHeight || null,
        crop: state.referenceArtwork.crop || null,
      },
    } : {}),
  }));
  // Keep large editable geometry in private Storage, with an immutable
  // version pointer committed only after the upload succeeds. Null clears old
  // inline maps under Firestore merge semantics (an empty map is ambiguous).
  const geometry = {
    layerStitchSettings: sanitized.layerStitchSettings,
    layerRails: sanitized.layerRails,
    layerCutMarks: sanitized.layerCutMarks,
  };
  const encoded = JSON.stringify(geometry);
  if (new Blob([JSON.stringify(sanitized)]).size > 600000) {
    const path = `${userId}/stitch-state/${encodeURIComponent(fileId)}/${crypto.randomUUID()}.json`;
    await uploadString(ref(storage, path), encoded, "raw", { contentType: "application/json" });
    sanitized.geometryFile = { path, version: 1 };
    sanitized.layerStitchSettings = null;
    sanitized.layerRails = null;
    sanitized.layerCutMarks = null;
  } else {
    sanitized.geometryFile = null;
  }
  const documentRef = doc(db, "accountData", userId, "stitchStates", fileId);
  const saved = { ...sanitized, updatedAt: serverTimestamp() };
  // Replace each supplied top-level map, rather than recursively merging its
  // numeric layer keys. A reorder/delete must remove the old rail ownership.
  // Omitted fields (reference art, pending jobs, etc.) remain untouched.
  await setDoc(documentRef, saved, { mergeFields: Object.keys(saved) });
};

export const loadStitchStateFromFirestore = async (userId, fileId, filePath = null) => {
  if (!userId || !fileId) return null;
  fileId = normalizeStitchFileId(fileId);
  try {
    const documentRef = doc(db, "accountData", userId, "stitchStates", fileId);
    let snap = await getDoc(documentRef);
    if (!snap.exists()) {
      // Legacy fallback order:
      // 1) ".svg"-suffixed key
      // 2) storage basename key (pre-svgMetadataId dashboard ids)
      //
      // The dashboard can switch from basename ids to svgMetadata doc ids once
      // privacy updates backfill svgMetadataId. If stitch state was saved before
      // that switch, it still sits under the basename key and would look "lost"
      // on reopen unless we probe and migrate it here.
      const basenameId = typeof filePath === "string"
        ? normalizeStitchFileId(filePath.split("/").pop() || "")
        : null;
      const candidateIds = [
        `${fileId}.svg`,
        ...(basenameId && basenameId !== fileId ? [basenameId, `${basenameId}.svg`] : []),
      ];
      let legacySnap = null;
      let legacyKey = null;
      for (const candidateId of candidateIds) {
        const candidateRef = doc(db, "accountData", userId, "stitchStates", candidateId);
        const candidateSnap = await getDoc(candidateRef);
        if (!candidateSnap.exists()) continue;
        legacySnap = candidateSnap;
        legacyKey = candidateId;
        break;
      }
      if (!legacySnap) return null;
      try {
        await setDoc(documentRef, legacySnap.data(), { merge: true });
        console.log("[loadStitchState] Migrated legacy stitch state:", {
          from: legacyKey,
          to: fileId,
        });
      } catch (e) {
        console.warn("[loadStitchState] Legacy migration write failed:", e.message);
      }
      snap = legacySnap;
    }
    let data = snap.data();
    if (data.geometryFile?.path) {
      const url = await getDownloadURL(ref(storage, data.geometryFile.path));
      const response = await fetch(url);
      if (!response.ok) throw new Error("Could not download the saved stitch geometry.");
      const geometry = await response.json();
      if (!geometry.layerRails || !geometry.layerStitchSettings || !geometry.layerCutMarks)
        throw new Error("The saved stitch geometry is incomplete.");
      data = { ...data, layerRails: geometry.layerRails,
        layerStitchSettings: geometry.layerStitchSettings, layerCutMarks: geometry.layerCutMarks };
    }
    return {
      version: data.version ?? 1,
      layerStitchSettings: data.layerStitchSettings || {},
      layerRails: data.layerRails || {},
      layerCutMarks: data.layerCutMarks || {},
      aiAnalysis: data.aiAnalysis || null,
      lastGeneratedFile: data.lastGeneratedFile || null,
      pendingConversion: data.pendingConversion || null,
      referenceArtwork: data.referenceArtwork || null,
      // null rather than {} so hydrateStitchState's truthiness check skips it
      // and leaves the editor's defaults alone.
      hoopSettings: data.hoopSettings || null,
    };
  } catch (e) {
    console.warn("[loadStitchState] Failed:", e.message);
    throw e;
  }
};

// Mark a conversion as in-progress on the user's account so a page refresh
// can detect it and auto-restart. Lightweight targeted write — does not touch
// any other stitch-state fields. `startedAt` is optional so callers can
// preserve an original timestamp across retries (defaults to `Date.now()`).
export const setPendingConversionInFirestore = async (userId, fileId, format, startedAt) => {
  if (!userId || !fileId || !format) return;
  try {
    const r = doc(db, "accountData", userId, "stitchStates", normalizeStitchFileId(fileId));
    await setDoc(
      r,
      { pendingConversion: { format, startedAt: startedAt || Date.now() } },
      { merge: true },
    );
  } catch (e) {
    console.warn("[setPendingConversion] Failed:", e.message);
  }
};

export const clearPendingConversionInFirestore = async (userId, fileId) => {
  if (!userId || !fileId) return;
  try {
    const r = doc(db, "accountData", userId, "stitchStates", normalizeStitchFileId(fileId));
    await setDoc(r, { pendingConversion: null }, { merge: true });
  } catch (e) {
    console.warn("[clearPendingConversion] Failed:", e.message);
  }
};

// Uploads a generated stitch file (DST/PES/JEF/VP3/EXP) to Storage so the user
// can re-download it later without rerunning the (slow, expensive) Cloud Run
// conversion. Returns the metadata blob the editor stores on the stitch state
// doc — caller should pair this with `saveStitchStateToFirestore` so the
// reference and per-layer settings end up on the same document.
export const saveStitchFileToStorage = async (
  userId,
  fileId,
  format,
  base64Data,
  extra = {}
) => {
  if (!userId || !fileId || !format || !base64Data) {
    throw new Error("saveStitchFileToStorage: missing required arg");
  }
  fileId = normalizeStitchFileId(fileId);
  const ext = String(format).toLowerCase();
  const filePath = `${userId}/stitchFiles/${fileId}.${ext}`;
  const sr = ref(storage, filePath);
  // Stitch formats are small binary files — `uploadString` with `"base64"`
  // accepts the same payload the editor already holds in memory, no Blob
  // ceremony required.
  const res = await uploadString(sr, base64Data, "base64", {
    contentType: "application/octet-stream",
    customMetadata: {
      svgtraceFileId: fileId,
      stitchFormat: ext,
    },
  });
  const downloadURL = await getDownloadURL(ref(storage, res.metadata.fullPath));
  // Approximate decoded byte length — every 4 base64 chars decode to 3 bytes,
  // minus 1 byte per `=` padding char. Avoids a second round-trip just to
  // surface a size in the UI.
  const padding = (base64Data.match(/=+$/) || [""])[0].length;
  const sizeBytes = Math.max(0, Math.floor((base64Data.length * 3) / 4) - padding);
  return {
    format: ext,
    filePath: res.metadata.fullPath,
    downloadURL,
    sizeBytes,
    generatedAt: new Date().toISOString(),
    metadata: extra?.metadata || null,
  };
};

// Fetches a previously-saved stitch file from Storage and returns it as a
// base64 string — same shape the Cloud Run service returns, so the editor can
// drop it into `conversionResult.data` without any conversion logic changes.
export const loadStitchFileFromStorage = async (downloadURL) => {
  if (!downloadURL) return null;
  try {
    const res = await fetch(downloadURL);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const buf = await res.arrayBuffer();
    // Convert binary → base64. Chunked to avoid call-stack overflow on the
    // ~hundreds-of-KB stitch files (apply() argument limit is ~65k items).
    const bytes = new Uint8Array(buf);
    let binary = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
  } catch (e) {
    console.warn("[loadStitchFile] Failed:", e?.message || e);
    return null;
  }
};
// ─────────────────────────────────────────────────────────────────────────────

// get svg from database by file path
export const getSVGFromDatabaseByFilePath = async (filePath) => {
  const svgRef = collection(db, METADATA_COLLECTION);
  const q = query(svgRef, where("filePath", "==", filePath));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    console.log(doc.id, " => ", doc.data());
  });
};

export const getPublicSVGs = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user found");

    const svgMetadataRef = collection(db, METADATA_COLLECTION);
    const q = query(svgMetadataRef, where("isPublic", "==", true), limit(20));

    const querySnapshot = await getDocs(q);

    const svgFiles = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    console.log({ svgFiles });
    return svgFiles;
  } catch (error) {
    console.error("Error fetching SVGs for user:", error);
    throw error;
  }
};

export const getSVGFromDatabase = async (docId) => {
  const docRef = doc(db, METADATA_COLLECTION, docId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    console.log("SVG data:", docSnap.data());
    return docSnap.data();
  } else {
    console.log("No such document!");
    return null;
  }
};

export const downloadSVGFromStorage = async (storageRef) => {
  // const storageRef = ref(storage, `${userId}/svg_generated/${fileName}`);
  // const url = await getDownloadURL(storageRef);

  const url = await getDownloadURL(storageRef);
  return url;
};

export const fetchSVGContentFromStorage = async (file) => {
  try {
    let storageRef;

    if (file.uri) {
      storageRef = file.uri;
    } else if (file.filePath) {
      storageRef = ref(storage, file.filePath);
    } else if (file.filepath) {
      storageRef = ref(storage, file.filepath);
    } else {
      throw new Error("File object missing storage reference or file path");
    }

    // Reject non-SVG paths early so a stray accountData entry pointing at a
    // DST/PES/etc. can never feed binary into the SVG parser.
    const fullPath = storageRef?.fullPath || "";
    if (fullPath && !/\.svg$/i.test(fullPath)) {
      throw new Error(
        `Storage path "${fullPath}" is not an SVG file — cannot open in the editor`
      );
    }

    const url = await getDownloadURL(storageRef);

    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`Failed to fetch SVG content: ${response.statusText}`);
    }

    const svgContent = await response.text();
    if (!/<svg[\s>]/i.test(svgContent.slice(0, 1024))) {
      throw new Error("Fetched content is not valid SVG");
    }
    return svgContent;
  } catch (error) {
    console.error("Error fetching SVG content from storage:", error);
    throw error;
  }
};

export const listAllSVGsInStorage = async () => {
  const user = auth.currentUser;
  const userId = user.uid;
  const listRef = ref(storage, `${userId}/${SVG_GENERATED_FOLDER}`);
  const res = await listAll(listRef);
  return res;
};
export const listAllUploadedSVGs = async () => {
  const user = auth.currentUser;
  const userId = user.uid;
  const listRef = ref(storage, `${userId}/${SVG_FOLDER}`);
  const res = await listAll(listRef);
  // const urls = await Promise.all(res.items.map(item => getDownloadURL(item)));
  return res;
};

// export const uploadedSVGsInStorage = async () => {
//   const user = auth.currentUser;
//   const userId = user.uid;
//   const listRef = ref(storage, `${userId}/svg`);
//   const res = await listAll(listRef);
//   return res;
// };

export const uploadedSVGsInStorage = async (inputDoc = null, pageSize = 96, includeGenerated = true) => {
  const user = auth.currentUser;
  console.log("\n🚀 UPLOADED_SVGS_STORAGE: Starting file fetch...");
  console.log(`   User: ${user ? user.uid : "No user"}`);
  console.log(`   Page size: ${pageSize}`);
  console.log(`   Include generated: ${includeGenerated}`);
  console.log(`   Input doc: ${inputDoc ? inputDoc.id : "None (first page)"}`);
  
  if (!user) {
    console.error("❌ UPLOADED_SVGS_STORAGE: No authenticated user found");
    throw new Error("No authenticated user found");
  }

  try {
    console.log(`\n📊 QUERY SETUP: Creating Firestore query...`);
    const filesRef = collection(db, "accountData", user.uid, FILES_SUBCOLLECTION);
    console.log(`   Collection path: accountData/${user.uid}/${FILES_SUBCOLLECTION}`);
    
    let q = query(filesRef, orderBy("timeCreated", "desc"), limit(pageSize));
    if (inputDoc) {
      console.log(`   Using pagination from doc: ${inputDoc.id}`);
      q = query(
        filesRef,
        orderBy("timeCreated", "desc"),
        startAfter(inputDoc),
        limit(pageSize)
      );
    }

    console.log(`\n📊 INITIAL QUERY: Executing Firestore query...`);
    const snapshot = await getDocs(q);
    console.log(`📊 INITIAL RESULT: Found ${snapshot.docs.length} files in cache`);
    
    if (snapshot.docs.length > 0) {
      console.log(`   Sample files from cache:`);
      snapshot.docs.slice(0, 3).forEach((doc, index) => {
        const data = doc.data();
        console.log(`   ${index + 1}. ${doc.id} - Type: ${data.type} - "${data.prompt || data.filepath}" (isPublic: ${data.isPublic})`);
      });
    }

    // If includeGenerated is true and we have space for more results, check for missing generated files
    if (includeGenerated && snapshot.docs.length < pageSize) {
      console.log(`\n🔄 LAZY CACHE: Need more files (${snapshot.docs.length}/${pageSize}), checking for missing generated files...`);
      await populateMissingGeneratedFiles(user.uid, snapshot.docs.length, pageSize);
      
      // Re-run the query to get the newly cached files
      console.log(`\n📊 UPDATED QUERY: Re-executing query after cache population...`);
      const updatedSnapshot = await getDocs(q);
      console.log(`📊 UPDATED RESULT: Now found ${updatedSnapshot.docs.length} files in cache`);
      
      if (updatedSnapshot.docs.length > snapshot.docs.length) {
        console.log(`✅ CACHE SUCCESS: Added ${updatedSnapshot.docs.length - snapshot.docs.length} files to cache!`);
      }
      
      return await processFiles(updatedSnapshot, user, pageSize);
    }
    
    console.log(`\n✅ CACHE STATUS: Using existing cache (${snapshot.docs.length} files)`);
    return await processFiles(snapshot, user, pageSize);
  } catch (error) {
    console.error("❌ UPLOADED_SVGS_STORAGE ERROR:", error);
    console.error(`   User: ${user?.uid}`);
    console.error(`   Error details:`, error.message);
    throw error;
  }
};

// Helper function to process files from snapshot
const processFiles = async (snapshot, user, pageSize = 12) => {
  const lastVisible = snapshot.docs[snapshot.docs.length - 1];

  console.log(`\n📁 PROCESS FILES: Processing ${snapshot.docs.length} files...`);

  // Only legacy files on this page need stitch-state discovery. Reading the
  // whole collection also downloads every saved rail/settings payload.
  const legacyIds = [...new Set(snapshot.docs.flatMap((fileDoc) => {
    const data = fileDoc.data();
    if (data.workflow || (data.filepath && !/\.svg$/i.test(data.filepath))) return [];
    return [data.svgMetadataId || data.id || fileDoc.id];
  }))];
  const stitchFileIds = new Set();
  const stitchStatesRef = collection(db, "accountData", user.uid, "stitchStates");
  await Promise.all(Array.from({ length: Math.ceil(legacyIds.length / 10) }, async (_, i) => {
    try {
      const stitchSnap = await getDocs(query(
        stitchStatesRef,
        where(documentId(), "in", legacyIds.slice(i * 10, i * 10 + 10))
      ));
      stitchSnap.docs.forEach((d) => stitchFileIds.add(d.id));
    } catch (e) {
      console.warn("Could not load stitch state IDs:", e);
    }
  }));

  const files = await Promise.all(
    snapshot.docs.map(async (doc, index) => {
      const data = doc.data();
      console.log(`   📄 File ${index + 1}/${snapshot.docs.length}: ${doc.id}`);
      console.log(`      Type: ${data.type || "uploaded"}`);
      console.log(`      FilePath: ${data.filepath}`);
      console.log(`      isPublic: ${data.isPublic} (original cached value)`);

      // Skip phantom entries created by the imageUploaded trigger for
      // non-SVG files (e.g. DST stitch artifacts). These should never
      // appear as dashboard tiles.
      const fp = data.filepath || "";
      if (fp && !/\.svg$/i.test(fp)) {
        console.warn(`      ⚠️ Skipping non-SVG filepath: ${fp}`);
        return null;
      }

      try {
        const storageRef = ref(storage, data.filepath);
        const url = await getDownloadURL(storageRef);

        const finalIsPublic = data.isPublic !== undefined ? data.isPublic : false;

        const fileId = data.svgMetadataId || data.id || doc.id;
        const workflow = data.workflow || (stitchFileIds.has(fileId) ? "stitch" : "vector");

        let referenceArtwork = data.referenceArtwork || null;
        if (referenceArtwork?.path && !referenceArtwork.url) {
          try {
            referenceArtwork = {
              ...referenceArtwork,
              url: await getDownloadURL(ref(storage, referenceArtwork.path)),
            };
          } catch (artworkError) {
            console.warn(`Could not load reference artwork for ${fileId}:`, artworkError.message);
          }
        }

        return {
          ...data,
          id: fileId,
          svgMetadataId: fileId,
          url,
          uri: storageRef,
          type: data.type || "uploaded",
          workflow,
          ownerUid: user.uid,
          isPublic: finalIsPublic,
          referenceArtwork,
        };
      } catch (error) {
        console.error(`      ❌ Error fetching URL for file ${doc.id}:`, error.message);
        return null;
      }
    })
  );

  const validFiles = files.filter((file) => file !== null);
  
  if (lastVisible) {
    console.log(`\n📄 PAGINATION: Last visible doc: ${lastVisible.id}`);
  }

  console.log(`\n📊 PROCESS COMPLETE:`);
  console.log(`   Total processed: ${snapshot.docs.length}`);
  console.log(`   Valid files: ${validFiles.length}`);
  console.log(`   Failed files: ${snapshot.docs.length - validFiles.length}`);
  console.log(`   Has more pages: ${snapshot.docs.length === pageSize}`);
  
  // Log privacy summary
  const generatedFiles = validFiles.filter(f => f.type === "generated");
  const uploadedFiles = validFiles.filter(f => f.type !== "generated");
  const publicFiles = validFiles.filter(f => f.isPublic === true);
  const privateFiles = validFiles.filter(f => f.isPublic === false);
  
  console.log(`\n🔒 PRIVACY SUMMARY:`);
  console.log(`   Generated files: ${generatedFiles.length} (can be public/private)`);
  console.log(`   Uploaded files: ${uploadedFiles.length} (always private)`);
  console.log(`   Public files: ${publicFiles.length}`);
  console.log(`   Private files: ${privateFiles.length}`);

  return {
    files: validFiles,
    lastDoc: lastVisible,
    hasMore: snapshot.docs.length === pageSize,
  };
};

// Helper function to populate missing generated files in cache
const populateMissingGeneratedFiles = async (userId, currentCount, pageSize) => {
  try {
    console.log(`\n🔍 CACHE POPULATE: Starting cache population for user ${userId}`);
    console.log(`   Current cached files: ${currentCount}`);
    console.log(`   Target page size: ${pageSize}`);
    console.log(`   Need to find: ${pageSize - currentCount} more files`);
    
    // Get generated files from svgMetadata that might not be in cache
    const svgMetadataRef = collection(db, METADATA_COLLECTION);
    const limitSize = pageSize - currentCount + 5; // Get a few extra to account for potential misses
    const svgQuery = query(
      svgMetadataRef, 
      where("ownerUid", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limitSize)
    );
    
    console.log(`📊 SVGMETADATA QUERY: Fetching up to ${limitSize} recent files from svgMetadata...`);
    const svgSnapshot = await getDocs(svgQuery);
    console.log(`📊 SVGMETADATA RESULT: Found ${svgSnapshot.docs.length} generated files in svgMetadata`);
    
    if (svgSnapshot.docs.length > 0) {
      console.log(`   Sample files from svgMetadata:`);
      svgSnapshot.docs.slice(0, 3).forEach((doc, index) => {
        const data = doc.data();
        console.log(`   ${index + 1}. ${doc.id} - "${data.prompt || 'No prompt'}" (isPublic: ${data.isPublic})`);
      });
    }
    
    // Check which ones are missing from accountData cache
    const accountDataRef = collection(db, "accountData", userId, FILES_SUBCOLLECTION);
    // Check only the candidates, not the account's entire generated library.
    const candidateIds = svgSnapshot.docs.map((fileDoc) => fileDoc.id);
    const existingSvgMetadataIds = new Set();
    await Promise.all(Array.from({ length: Math.ceil(candidateIds.length / 10) }, async (_, i) => {
      const existingSnapshot = await getDocs(query(
        accountDataRef,
        where("svgMetadataId", "in", candidateIds.slice(i * 10, i * 10 + 10))
      ));
      existingSnapshot.docs.forEach((fileDoc) => {
        existingSvgMetadataIds.add(fileDoc.data().svgMetadataId);
      });
    }));
    
    // Add missing files to cache
    const missingFiles = svgSnapshot.docs.filter(doc => !existingSvgMetadataIds.has(doc.id));
    const filesToAdd = missingFiles.slice(0, pageSize - currentCount);
    
    console.log(`\n🔄 CACHE ANALYSIS:`);
    console.log(`   Total in svgMetadata: ${svgSnapshot.docs.length}`);
    console.log(`   Already cached: ${existingSvgMetadataIds.size}`);
    console.log(`   Missing from cache: ${missingFiles.length}`);
    console.log(`   Will add to cache: ${filesToAdd.length}`);
    
    if (filesToAdd.length > 0) {
      console.log(`\n⚡ CACHE POPULATION: Adding ${filesToAdd.length} missing files...`);
      for (let i = 0; i < filesToAdd.length; i++) {
        const missingFile = filesToAdd[i];
        console.log(`   📝 Adding file ${i + 1}/${filesToAdd.length}: ${missingFile.id}`);
        await addGeneratedFileToCache(missingFile);
      }
    } else {
      console.log(`\n✅ CACHE STATUS: No missing files to add - cache is up to date!`);
    }
    
    console.log(`\n🏁 CACHE POPULATE: Finished populating missing generated files`);
  } catch (error) {
    console.error(`❌ CACHE POPULATE ERROR: Failed to populate missing generated files:`, error);
    console.error(`   User ID: ${userId}`);
    console.error(`   Error details:`, error.message);
    // Don't throw - this is cache population, shouldn't break the main flow
  }
};

// make a delete file function
export const deleteFileInStorage = async (fileName) => {
  const user = auth.currentUser;
  const userId = user.uid;
  const storageRef = ref(storage, `${userId}/${SVG_GENERATED_FOLDER}/${fileName}`);
  // use listAll to get all the files in the folder
  await deleteObject(storageRef);
};

// make a call to a cloud function
export const analyzeImageSEO = async (image64) => {
  const callFunction = httpsCallable(functions, "analyzeImageForSEO");
  const prompt =
    "Can you generate a 150 char SEO description for the attached image?";
  const resp = await callFunction({ image64, prompt });
  return resp;
};

// export const updateSVGInStorage = async (storageRef, data, svgId) => {
//   const user = auth.currentUser;
//   const userId = user.uid;
//   console.log("userId: ", userId);
//   const res = await uploadString(storageRef, data, "data_url");
//   console.log("SVG updated in Storage", res.doc.id);

//   // Fetch the updated SVG
//   const svgMetadataRef = collection(db, METADATA_COLLECTION);
//   const docRef = doc(svgMetadataRef, svgId);
//   const docSnap = await getDoc(docRef);

//   if (docSnap.exists()) {
//     console.log("Document data:", docSnap.data());
//     return { id: docSnap.id, ...docSnap.data() };
//   } else {
//     console.log("No such document!");
//     throw new Error("No such document!");
//   }
// };

// updatesvginstorage

export const fetchSVGsForUser = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user found");

    const svgMetadataRef = collection(db, METADATA_COLLECTION);
    const q = query(svgMetadataRef, where("ownerUid", "==", user.uid));

    const querySnapshot = await getDocs(q);

    const svgFiles = querySnapshot.docs.map((doc) => {
      return {
        id: doc.id,
        ...doc.data(),
      };
    });

    return svgFiles;
  } catch (error) {
    console.error("Error fetching SVGs for user:", error);
    throw error;
  }
};

export const fetchFilesWithUrls = async () => {
  try {
    let svgFiles = await fetchSVGsForUser();

    // Sort by newest to oldest based on the createdAt timestamp
    svgFiles = svgFiles.sort(
      (a, b) => b.createdAt.seconds - a.createdAt.seconds
    );

    const storage = getStorage();

    const filesWithUrls = await Promise.all(
      svgFiles.map(async (file) => {
        try {
          const storageRef = ref(storage, file.filePath);
          const url = await getDownloadURL(storageRef);
          return {
            ...file,
            uri: storageRef,
            type: "generated",
            url, // Add the URL to the file object
            date: new Date(file.createdAt.seconds * 1000).toLocaleDateString(
              "en-US",
              {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
                second: "numeric",
              }
            ),
            name: file.prompt,
          };
        } catch (error) {
          console.error(`Error fetching URL for file ${file.filePath}:`, error);
          return null; // Or handle differently
        }
      })
    );

    // Filter out any null values that were returned due to errors
    return filesWithUrls.filter((file) => file !== null);
  } catch (error) {
    console.error("Error fetching files:", error);
    throw error;
  }
};

// Helper function to process Algolia results and add Firebase URLs
const processAlgoliaFilesWithUrls = async (algoliaResult) => {
  let svgFiles = algoliaResult.files;
  
  // Filter out files without valid filePath first
  svgFiles = svgFiles.filter((file) => file && file.filePath);
  
  // Sort by newest to oldest based on the createdAt timestamp (with fallback)
  svgFiles = svgFiles.sort((a, b) => {
    const aTime = a.createdAt?.seconds || 0;
    const bTime = b.createdAt?.seconds || 0;
    return bTime - aTime;
  });

  const storage = getStorage();

  const filesWithUrls = await Promise.all(
    svgFiles.map(async (file) => {
      try {
        // Use publicUrl if available (direct access, no auth needed)
        // Otherwise fall back to getDownloadURL for backward compatibility
        let url;
        if (file.publicUrl) {
          url = file.publicUrl;
        } else {
          const storageRef = ref(storage, file.filePath);
          url = await getDownloadURL(storageRef);
        }
        
        const storageRef = ref(storage, file.filePath);
        const createdAtSeconds = file.createdAt?.seconds;
        return {
          ...file,
          uri: storageRef,
          url, // Add the URL to the file object
          date: createdAtSeconds 
            ? new Date(createdAtSeconds * 1000).toLocaleDateString(
                "en-US",
                {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                }
              )
            : undefined,
          name: file.prompt,
        };
      } catch (error) {
        console.error(`Error fetching URL for file ${file.filePath}:`, error.message);
        return null;
      }
    })
  );

  // Filter out any null values that were returned due to errors
  const validFiles = filesWithUrls.filter((file) => file !== null);
  
  return {
    files: validFiles,
    hasMore: algoliaResult.hasMore,
    currentPage: algoliaResult.currentPage,
    totalPages: algoliaResult.totalPages,
    totalHits: algoliaResult.totalHits,
  };
};

export const fetchPublicFilesWithUrls = async (page = 0, hitsPerPage = 24) => {
  try {
    const algoliaResult = await getPublicSVGsWithAlgolia(page, hitsPerPage);
    return await processAlgoliaFilesWithUrls(algoliaResult);
  } catch (error) {
    console.error("Error fetching files:", error);
    throw error;
  }
};

// NEW: Fetch public files directly from Firestore (faster, real-time, no sync delay)
export const fetchRecentPublicFilesFromFirestore = async (limitCount = 24, lastDocSnapshot = null) => {
  try {
    console.log("📦 Fetching public files directly from Firestore");
    const svgMetadataRef = collection(db, METADATA_COLLECTION);
    const storage = getStorage();
    
    let q;
    if (lastDocSnapshot) {
      q = query(
        svgMetadataRef,
        where("isPublic", "==", true),
        orderBy("createdAt", "desc"),
        startAfter(lastDocSnapshot),
        limit(limitCount)
      );
    } else {
      q = query(
        svgMetadataRef,
        where("isPublic", "==", true),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );
    }
    
    const snapshot = await getDocs(q);
    console.log(`✅ Firestore returned ${snapshot.docs.length} public files`);
    
    const files = await Promise.all(
      snapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        
        // Skip files without filePath - they can't be displayed
        if (!data.filePath) {
          console.warn(`Skipping file ${docSnap.id} - no filePath`);
          return null;
        }
        
        // Create storage reference for this file
        const storageRef = ref(storage, data.filePath);
        
        // Use publicUrl if available, otherwise fall back to getDownloadURL
        let url;
        if (data.publicUrl) {
          url = data.publicUrl;
          console.log(`📦 Using publicUrl for ${docSnap.id}:`, url);
        } else {
          try {
            url = await getDownloadURL(storageRef);
            console.log(`📦 Using getDownloadURL for ${docSnap.id}:`, url);
          } catch (error) {
            console.error("Error getting download URL for:", data.filePath, error);
            return null;
          }
        }
        
        const createdAtSeconds = data.createdAt?.seconds;
        return {
          id: docSnap.id,
          ...data,
          url,
          uri: storageRef, // Always set uri from valid storageRef
          createdAtSeconds,
          date: createdAtSeconds
            ? new Date(createdAtSeconds * 1000).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : null,
        };
      })
    );
    
    // Filter out any null entries (files that failed validation or URL fetch)
    const validFiles = files.filter(file => file !== null && file.uri);
    
    console.log(`📦 Returning ${validFiles.length} valid files with URLs:`, 
      validFiles.map(f => ({ id: f.id, url: f.url, hasUri: !!f.uri }))
    );
    
    return {
      files: validFiles,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount,
      totalHits: null, // Firestore doesn't give total count efficiently
    };
  } catch (error) {
    console.error("Error fetching public files from Firestore:", error);
    throw error;
  }
};

// New function for search that also processes URLs consistently
export const searchPublicFilesWithUrls = async (query, page = 0, hitsPerPage = 24) => {
  try {
    console.log("🔍 Searching files with URLs for:", query);
    const algoliaResult = await searchPublicSVGsWithAlgolia(query, page, hitsPerPage);
    const processedResult = await processAlgoliaFilesWithUrls(algoliaResult);
    console.log("✅ Search with URLs completed");
    return processedResult;
  } catch (error) {
    console.error("Error searching files with URLs:", error);
    throw error;
  }
};

/**
 * Looks for a copy of `designId` this user has already taken, so opening the
 * same community design twice reuses their file instead of duplicating the
 * artwork in Storage.
 *
 * Returns null on any failure — a missing index, a tightened rule, or a doc
 * whose Storage object has since been deleted. Falling back to a fresh copy is
 * always safe; the only cost is the duplicate this lookup exists to avoid.
 */
const findExistingFork = async (uid, designId) => {
  try {
    const snap = await getDocs(
      query(
        collection(db, METADATA_COLLECTION),
        where("ownerUid", "==", uid),
        where("forkedFrom.designId", "==", designId),
        limit(1)
      )
    );
    if (snap.empty) return null;

    const forkDoc = snap.docs[0];
    const data = forkDoc.data();
    if (!data.filePath) return null;

    const uri = ref(storage, data.filePath);
    return {
      id: forkDoc.id,
      svgMetadataId: forkDoc.id,
      name: data.seoTitle || data.prompt || "Community design",
      filepath: data.filePath,
      uri,
      url: await getDownloadURL(uri),
      type: data.type || "uploaded",
      workflow: data.workflow || "stitch",
      ownerUid: uid,
      isPublic: data.isPublic ?? false,
    };
  } catch (error) {
    console.warn("Could not check for an existing fork:", error);
    return null;
  }
};

/**
 * Reads the shared stitch settings a creator published alongside a design, so
 * someone previewing it starts from a tuned design rather than bare artwork.
 *
 * Returns null when the design was published as artwork only, or once the
 * creator unpublishes it and the trigger tears this subcollection down.
 */
export const loadPublicStitchState = async (designId) => {
  if (!designId) return null;
  try {
    const snap = await getDoc(
      doc(db, METADATA_COLLECTION, designId, "publicStitchState", "state")
    );
    if (!snap.exists()) return null;
    let state = snap.data();
    if (state.geometryChunks) {
      if (!Array.isArray(state.geometryChunks) || !state.geometryChunks.length ||
          state.geometryChunks.some((id) => typeof id !== "string" || !/^geometry-[a-f0-9]{64}-\d+$/.test(id))) {
        throw new Error("Invalid shared stitch geometry manifest.");
      }
      const chunks = await Promise.all(state.geometryChunks.map((id) =>
        getDoc(doc(db, METADATA_COLLECTION, designId, "publicStitchState", id))
      ));
      if (chunks.some((chunk) => !chunk.exists() || typeof chunk.data().data !== "string")) {
        throw new Error("Shared stitch geometry is incomplete.");
      }
      const geometry = JSON.parse(chunks.map((chunk) => chunk.data().data).join(""));
      if (!geometry.layerRails || !geometry.layerStitchSettings || !geometry.layerCutMarks) {
        throw new Error("Shared stitch geometry is incomplete.");
      }
      state = { ...state, layerRails: geometry.layerRails,
        layerStitchSettings: geometry.layerStitchSettings, layerCutMarks: geometry.layerCutMarks };
    }
    return {
      version: state.version ?? 1,
      layerStitchSettings: state.layerStitchSettings || {},
      layerRails: state.layerRails || {},
      layerCutMarks: state.layerCutMarks || {},
      aiAnalysis: state.aiAnalysis || null,
      hoopSettings: state.hoopSettings || null,
    };
  } catch (error) {
    console.warn("Could not load shared stitch settings:", error);
    return null;
  }
};

/**
 * Saves the design currently open in the editor into the signed-in user's
 * account as their own private copy.
 *
 * Used when someone previewing a community design decides to keep it. The
 * artwork uploaded is whatever sits in the editor right now rather than the
 * published original, so anything they changed while previewing survives.
 *
 * Deliberately private regardless of the user's default-privacy preference —
 * republishing someone else's design should take a separate deliberate act, and
 * forkedFrom keeps the original creator credited when it does.
 */
export const saveCommunityDesignCopy = async ({ sourceFile, svgDataUrl, stitchState }) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");
  if (!sourceFile?.id) throw new Error("Design is missing its source");
  if (!svgDataUrl) throw new Error("No artwork to save");

  const alreadySaved = await findExistingFork(user.uid, sourceFile.id);
  if (alreadySaved) return alreadySaved;

  const { res } = await uploadSVGToStorage(svgDataUrl, SVG_FOLDER);
  const filePath = res.metadata.fullPath;

  const docRef = await writeSVGToDatabase(
    filePath,
    sourceFile.prompt || "",
    { title: sourceFile.seoTitle || "", description: sourceFile.seoDescription || "" },
    false,
    "uploaded",
    "stitch",
    {
      // Points at the immediate parent, not the root of the chain. Walking back
      // through each parent's own forkedFrom reconstructs the full lineage, and
      // the doc's createdAt is when this copy was taken. seoTitle is copied in
      // so the credit line still renders if the original is later deleted.
      forkedFrom: {
        designId: sourceFile.id,
        ownerUid: sourceFile.ownerUid || null,
        seoTitle: sourceFile.seoTitle || "",
      },
    }
  );

  // Non-fatal: losing the settings costs the user their tuning, not the design.
  if (stitchState) {
    try {
      await saveStitchStateToFirestore(user.uid, docRef.id, stitchState);
    } catch (error) {
      console.warn("Could not save stitch settings for the copy:", error);
    }
  }

  const uri = ref(storage, filePath);
  return {
    id: docRef.id,
    svgMetadataId: docRef.id,
    name: sourceFile.seoTitle || sourceFile.name || "Community design",
    filepath: filePath,
    uri,
    url: await getDownloadURL(uri),
    type: "uploaded",
    workflow: "stitch",
    ownerUid: user.uid,
    isPublic: false,
  };
};

// Function to search for a specific creator's artwork
export const searchCreatorFilesWithUrls = async (ownerUid, page = 0, hitsPerPage = 24) => {
  try {
    console.log("👨‍🎨 Searching creator files with URLs for:", ownerUid);
    const algoliaResult = await searchPublicSVGsByCreator(ownerUid, page, hitsPerPage);
    const processedResult = await processAlgoliaFilesWithUrls(algoliaResult);
    console.log("✅ Creator search with URLs completed");
    return processedResult;
  } catch (error) {
    console.error("Error searching creator files with URLs:", error);
    throw error;
  }
};

// Function to delete a file from both Firebase Storage and Firestore
export const deleteFileFromStorage = async (storageRef) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");
  
  // Get the file path from the storage reference
  const filePath = storageRef._location?.path_ || storageRef.fullPath;
  
  // Delete from Firebase Storage
  await deleteObject(storageRef);
  
  // Also delete the svgMetadata document from Firestore
  // This will trigger onSvgMetadataDeleted which cleans up the public folder
  try {
    const svgMetadataRef = collection(db, METADATA_COLLECTION);
    const q = query(
      svgMetadataRef,
      where("ownerUid", "==", user.uid),
      where("filePath", "==", filePath)
    );
    const querySnapshot = await getDocs(q);
    
    for (const docSnap of querySnapshot.docs) {
      await deleteDoc(docSnap.ref);
      console.log(`Deleted svgMetadata document: ${docSnap.id}`);
    }
  } catch (error) {
    console.error("Error deleting svgMetadata document:", error);
    // Don't throw - storage file is already deleted
  }
};

// export const updateFilePublicStatus = (storageRef, isPublic) => {
//   const user = auth.currentUser;
//   if (!user) throw new Error("User not authenticated");

//   const filePath = storageRef._location.path_;
//   console.log(filePath);
//   const svgMetadataRef = collection(db, METADATA_COLLECTION);
//   const q = query(
//     svgMetadataRef,
//     where("ownerUid", "==", user.uid),
//     where("filePath", "==", filePath)
//   );

//   getDocs(q)
//     .then((querySnapshot) => {
//       querySnapshot.forEach((doc) => {
//         const svgDocRef = doc.ref;
//         updateDoc(svgDocRef, { isPublic: isPublic })
//           .then(() => {
//             console.log(
//               `File ${filePath} public status updated to ${isPublic}`
//             );
//           })
//           .catch((error) => {
//             console.error("Error updating document: ", error);
//           });
//       });
//     })
//     .catch((error) => {
//       console.error("Error getting documents: ", error);
//     });
// };
export const updateFilePublicStatus = async (storageRef, isPublic) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  const filePath = storageRef._location.path_;
  let svgMetadataDocId = null;

  // Update primary svgMetadata collection (source of truth)
  const svgMetadataRef = collection(db, METADATA_COLLECTION);
  const q = query(
    svgMetadataRef,
    where("ownerUid", "==", user.uid),
    where("filePath", "==", filePath)
  );

  try {
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      // Legacy file - add to svgMetadata on-the-fly
      console.log("🔄 LEGACY FILE: Adding to svgMetadata on-the-fly...");
      
      // Allow legacy files to be made public
      const docRef = await writeSVGToDatabase(
        filePath,
        "", // No prompt for legacy files
        { title: "", description: "" }, // Empty SEO data
        isPublic, // Use the requested public status
        "uploaded" // Assume legacy files are uploaded
      );
      
      console.log(`✅ LEGACY FILE: Added to svgMetadata with ID ${docRef.id}`);
      return { isPublic, svgMetadataDocId: docRef.id };
    }

    // Existing file - normal update flow
    for (const doc of querySnapshot.docs) {
      svgMetadataDocId = doc.id;
      const svgDocRef = doc.ref;
      const fileData = doc.data();
      
      // Allow all file types to be made public
      try {
        await updateDoc(svgDocRef, { isPublic: isPublic });
        console.log(`✅ Updated existing file ${doc.id} (type: ${fileData.type}) isPublic to ${isPublic}`);
      } catch (error) {
        console.error(`Error updating svgMetadata doc ${doc.id}:`, error);
        throw error;
      }
    }

    // Also update accountData collection (cache layer) to keep in sync
    if (svgMetadataDocId) {
      try {
        const accountDataRef = collection(db, "accountData", user.uid, FILES_SUBCOLLECTION);
        const accountDataQuery = query(
          accountDataRef,
          where("svgMetadataId", "==", svgMetadataDocId)
        );
        
        console.log(`🔄 SYNC: Looking for accountData docs with svgMetadataId: ${svgMetadataDocId}`);
        const accountDataSnapshot = await getDocs(accountDataQuery);
        console.log(`🔄 SYNC: Found ${accountDataSnapshot.docs.length} docs by svgMetadataId`);
        
        for (const accountDoc of accountDataSnapshot.docs) {
          await updateDoc(accountDoc.ref, { isPublic: isPublic });
          console.log(`🔄 SYNC: Updated cache ${accountDoc.id} isPublic to ${isPublic}`);
        }
        
        // Also try to find by filepath as fallback (for both uploaded and generated files)
        if (accountDataSnapshot.empty) {
          console.log(`🔄 SYNC: No docs found by svgMetadataId, trying filepath fallback for: ${filePath}`);
          
          // Try to find by filepath regardless of type
          const fallbackQuery = query(
            accountDataRef,
            where("filepath", "==", filePath)
          );
          const fallbackSnapshot = await getDocs(fallbackQuery);
          
          console.log(`🔄 SYNC: Found ${fallbackSnapshot.docs.length} docs by filepath`);
          
          for (const accountDoc of fallbackSnapshot.docs) {
            const currentData = accountDoc.data();
            console.log(`🔄 SYNC: Updating doc ${accountDoc.id} (current type: ${currentData.type})`);
            
            await updateDoc(accountDoc.ref, { 
              isPublic: isPublic,
              svgMetadataId: svgMetadataDocId // Add missing reference
            });
            console.log(`🔄 SYNC: Updated cache via fallback ${accountDoc.id} isPublic to ${isPublic}`);
          }
          
          // If still no docs found, this might be a legacy file not in accountData cache
          if (fallbackSnapshot.empty) {
            console.log(`⚠️ SYNC: File ${filePath} not found in accountData cache - this is a legacy file that exists only in storage`);
          }
        }
      } catch (accountDataError) {
        console.error("❌ Cache sync error:", accountDataError);
        // Don't throw - accountData is cache layer, svgMetadata update succeeded
      }
    }

    return { isPublic, svgMetadataDocId };
  } catch (error) {
    console.error("❌ Error updating file public status:", error);
    throw error;
  }
};

// Helper function to add generated file to accountData cache on-the-fly
export const addGeneratedFileToCache = async (svgMetadataDoc) => {
  const user = auth.currentUser;
  if (!user) {
    console.warn("🚫 addGeneratedFileToCache: No authenticated user");
    return;
  }

  try {
    const data = svgMetadataDoc.data();
    const accountDataRef = collection(db, "accountData", user.uid, FILES_SUBCOLLECTION);
    
    const cacheData = {
      id: svgMetadataDoc.id,
      filepath: data.filePath,
      timeCreated: data.createdAt,
      type: "generated",
      isPublic: data.isPublic !== undefined ? data.isPublic : true, // Default to true for generated files
      prompt: data.prompt || "",
      svgMetadataId: svgMetadataDoc.id,
      seoTitle: data.seoTitle || "",
      seoDescription: data.seoDescription || "",
    };
    
    console.log(`📝 CACHE ADD: Adding file to cache for user ${user.uid}`);
    console.log(`   File ID: ${svgMetadataDoc.id}`);
    console.log(`   Prompt: "${data.prompt || 'No prompt'}"`);
    console.log(`   isPublic: ${cacheData.isPublic} (original: ${data.isPublic})`);
    console.log(`   FilePath: ${data.filePath}`);
    
    await addDoc(accountDataRef, cacheData);
    
    console.log(`✅ CACHE SUCCESS: File ${svgMetadataDoc.id} added to cache successfully`);
  } catch (error) {
    console.error(`❌ CACHE ERROR: Failed to add file ${svgMetadataDoc.id} to cache:`, error);
    // Don't throw - this is just cache population
  }
};

// Utility function to verify sync between collections
export const verifySyncBetweenCollections = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  try {
    // Get all generated files from both collections
    const svgMetadataRef = collection(db, METADATA_COLLECTION);
    const svgQuery = query(svgMetadataRef, where("ownerUid", "==", user.uid));
    const svgSnapshot = await getDocs(svgQuery);
    
    const accountDataRef = collection(db, "accountData", user.uid, FILES_SUBCOLLECTION);
    const accountQuery = query(accountDataRef, where("type", "==", "generated"));
    const accountSnapshot = await getDocs(accountQuery);
    
    const svgMetadataFiles = new Map();
    svgSnapshot.docs.forEach(doc => {
      const data = doc.data();
      svgMetadataFiles.set(doc.id, {
        isPublic: data.isPublic,
        filePath: data.filePath,
        prompt: data.prompt
      });
    });
    
    const accountDataFiles = new Map();
    accountSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.svgMetadataId) {
        accountDataFiles.set(data.svgMetadataId, {
          isPublic: data.isPublic,
          filepath: data.filepath,
          prompt: data.prompt
        });
      }
    });
    
    console.log(`svgMetadata: ${svgMetadataFiles.size} files`);
    console.log(`accountData: ${accountDataFiles.size} generated files`);
    
    // Check for mismatches
    const mismatches = [];
    for (const [id, svgData] of svgMetadataFiles) {
      const accountData = accountDataFiles.get(id);
      
      if (!accountData) {
        mismatches.push({
          id,
          type: 'missing_in_account_data',
          svgData
        });
      } else if (svgData.isPublic !== accountData.isPublic) {
        mismatches.push({
          id,
          type: 'privacy_mismatch',
          svgData,
          accountData
        });
      }
    }
    
    if (mismatches.length > 0) {
      console.warn(`Found ${mismatches.length} sync mismatches:`, mismatches);
    } else {
      console.log("✅ All collections are in sync!");
    }
    
    return {
      svgMetadataCount: svgMetadataFiles.size,
      accountDataCount: accountDataFiles.size,
      mismatches
    };
  } catch (error) {
    console.error("Error verifying sync:", error);
    throw error;
  }
};

export const incrementDownloadCount = async (storageRef) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  const filePath = storageRef._location.path_;
  const svgMetadataRef = collection(db, METADATA_COLLECTION);
  const q = query(
    svgMetadataRef,
    where("ownerUid", "==", user.uid),
    where("filePath", "==", filePath)
  );

  const querySnapshot = await getDocs(q);

  querySnapshot.forEach(async (doc) => {
    const svgDocRef = doc.ref;
    const currentDownloadCount = doc.data().downloadCount || 0;
    await updateDoc(svgDocRef, { downloadCount: currentDownloadCount + 1 });
    console.log(`Download count for file ${filePath} incremented successfully`);
  });
};
/**
 * Increment the view or download count for a file in Firestore
 * @param {string} docId - The id of the file metadata document in firebase
 * @param {"view" | "download"} type - The type of count to increment (view or download)
 * @returns {Promise<void>}
 */
export const incrementViewOrDownloadCount = async (docId, type) => {
  const svgDocRef = doc(db, METADATA_COLLECTION, docId);

  if (type === "view") await updateDoc(svgDocRef, { viewCount: increment(1) });
  else if (type === "download")
    await updateDoc(svgDocRef, { downloadCount: increment(1) });
  console.log(`${type} count for file ${docId} incremented successfully`);
};

/**
 * Update user survey data in Firestore
 * @param {string} userId - The user's UID
 * @param {Object} surveyData - Survey response data
 * @returns {Promise<void>}
 */
export const updateUserSurveyData = async (userId, surveyData) => {
  try {
    const db = getFirestore();
    
    // Try to get the user document directly by document ID
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // If direct lookup fails, try searching by uid field (fallback)
      console.log("Direct lookup failed, trying uid field search...");
      const usersQuery = query(collection(db, "users"), where("uid", "==", userId));
      const querySnapshot = await getDocs(usersQuery);
      
      if (querySnapshot.empty) {
        throw new Error(`User document not found for userId: ${userId}`);
      }
      
      // Use the first matching document
      const userDocFromQuery = querySnapshot.docs[0];
      const userDocRefFromQuery = userDocFromQuery.ref;
      
      const updateData = {
        surveyData: {
          howDidYouHear: surveyData.howDidYouHear,
          currentSoftware: surveyData.currentSoftware,
          completedAt: serverTimestamp(),
        },
        surveyCompleted: true,
        updatedAt: serverTimestamp(),
      };
      
      await updateDoc(userDocRefFromQuery, updateData);
      console.log(`✅ Survey data updated for user ${userId} (via uid field search)`);
      return { success: true };
    }
    
    // Direct document lookup succeeded
    const updateData = {
      surveyData: {
        howDidYouHear: surveyData.howDidYouHear,
        currentSoftware: surveyData.currentSoftware,
        completedAt: serverTimestamp(),
      },
      surveyCompleted: true,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(userDocRef, updateData);
    console.log(`✅ Survey data updated for user ${userId}`);
    
    return { success: true };
  } catch (error) {
    console.error("❌ Error updating user survey data:", error);
    throw error;
  }
};

/**
 * Create or update user document in Firestore for OAuth signups
 * @param {string} uid - User UID
 * @param {string} email - User email
 * @param {Object} utmData - Optional UTM tracking data
 */
export const createOAuthUserDocument = async (uid, email, utmData = null) => {
  try {
    const db = getFirestore();
    const userDocRef = doc(db, "users", uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // Create new user document
      const createdAt = new Date();
      // Record which app the user signed up from so backend triggers
      // can send the correct branded emails / sequences.
      const signupApp = getCurrentAppId();
      const userData = {
        uid: uid,
        email: email,
        createdAt: createdAt,
        signupApp,
        lastActiveApp: signupApp,
        apps: [signupApp],
        ...(getLandingExperiment() ? { landingExperiment: getLandingExperiment() } : {}),
      };
      
      // Add UTM tracking data if provided
      if (utmData) {
        userData.utmData = utmData;
      }
      
      await setDoc(userDocRef, userData);
      console.log("OAuth user document created:", uid);
    } else {
      const existingData = userDoc.data();
      const currentApp = getCurrentAppId();

      const updates = {
        lastActiveApp: currentApp,
        apps: arrayUnion(currentApp),
        updatedAt: serverTimestamp(),
      };

      if (utmData && !existingData.utmData) {
        updates.utmData = utmData;
      }

      await updateDoc(userDocRef, updates);
      console.log("OAuth user document updated (app: %s):", currentApp, uid);
    }
  } catch (error) {
    console.error("Error creating/updating OAuth user document:", error);
    // Don't throw - this shouldn't block the sign-in process
  }
};

/**
 * Check if we're running in a native mobile environment (iOS/Android via Capacitor)
 * Native apps need redirect-based OAuth because popups don't work well
 */
const isNativeMobile = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    // Capacitor not available, assume web
    return false;
  }
};

/**
 * Check if we're on mobile web (browser on phone/tablet)
 * Mobile web browsers also have issues with popups
 */
const isMobileWeb = () => {
  if (typeof window === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Determine if we should use redirect-based OAuth instead of popup.
 * Capacitor iOS with server.hostname set to the Firebase authDomain
 * can handle both popup and redirect. We prefer popup everywhere
 * because it stays within the WebView and avoids navigation issues.
 * Redirect is only used as a fallback when popup is blocked.
 */
const shouldUseRedirect = () => {
  return false;
};

export const signInWithGoogle = async () => {
  if (isNativeMobile()) {
    console.log("🔐 Google sign-in: Using NATIVE flow (Capacitor)");
    return mobileAuth.signInWithGoogle();
  }

  const provider = new GoogleAuthProvider();
  // Always show the account chooser instead of silently reusing the last account.
  provider.setCustomParameters({ prompt: "select_account" });
  const isMobile = isMobileWeb();
  
  try {
    console.log("🔐 Google sign-in: Using POPUP flow (web)");
    try {
      const result = await signInWithPopup(auth, provider);
      result.additionalUserInfo = getAdditionalUserInfo(result);
      if (result.additionalUserInfo?.isNewUser) trackLandingAccountCreated();
      console.log("🔐 Google sign-in successful:", result.user?.email);
      return result;
    } catch (popupError) {
      if (popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/popup-closed-by-user' ||
          (isMobile && popupError.code === 'auth/internal-error')) {
        console.log("🔐 Popup failed, falling back to redirect...", popupError.code);
        sessionStorage.setItem('oauthRedirectPending', 'google');
        await signInWithRedirect(auth, provider);
        return null;
      }
      throw popupError;
    }
  } catch (error) {
    console.error("🔐 Google sign-in error:", error.code, error.message);
    throw error;
  }
};

export const signInWithApple = async () => {
  if (isNativeMobile()) {
    console.log("🔐 Apple sign-in: Using NATIVE flow (Capacitor)");
    return mobileAuth.signInWithApple();
  }

  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");

  const isMobile = isMobileWeb();

  try {
    console.log("🔐 Apple sign-in: Using POPUP flow (web)");
    try {
      const result = await signInWithPopup(auth, provider);
      result.additionalUserInfo = getAdditionalUserInfo(result);
      if (result.additionalUserInfo?.isNewUser) trackLandingAccountCreated();
      console.log("🔐 Apple sign-in successful:", result.user?.email);
      return result;
    } catch (popupError) {
      if (popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/popup-closed-by-user' ||
          (isMobile && popupError.code === 'auth/internal-error')) {
        console.log("🔐 Popup failed, falling back to redirect...", popupError.code);
        sessionStorage.setItem('oauthRedirectPending', 'apple');
        await signInWithRedirect(auth, provider);
        return null;
      }
      throw popupError;
    }
  } catch (error) {
    console.error("🔐 Apple sign-in error:", error.code, error.message);
    throw error;
  }
};

/**
 * Handle OAuth redirect result
 * Should be called on app initialization to check if we're returning from OAuth redirect
 */
export const handleOAuthRedirectResult = async () => {
  console.log("🔐 Checking for OAuth redirect result...");
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      result.additionalUserInfo = getAdditionalUserInfo(result);
      if (result.additionalUserInfo?.isNewUser) trackLandingAccountCreated();
    }
    if (result) {
      console.log("🔐 ✅ OAuth redirect result found:", result.user?.email);
      return result;
    }
    console.log("🔐 No OAuth redirect result (normal page load or popup flow was used)");
    return null;
  } catch (error) {
    console.error("🔐 ❌ Error handling OAuth redirect:", error.code, error.message);
    // Common errors:
    // - auth/popup-blocked: popup was blocked
    // - auth/unauthorized-domain: domain not authorized in Firebase console
    // - auth/operation-not-allowed: OAuth not enabled
    throw error;
  }
};

// Utility function for testing lazy cache approach
export const testLazyCacheSystem = async () => {
  const user = auth.currentUser;
  if (!user) {
    console.error("❌ No authenticated user");
    return;
  }

  console.log("🧪 TESTING LAZY CACHE SYSTEM");
  console.log("=" .repeat(50));
  console.log(`👤 User: ${user.uid}`);
  console.log(`📅 Test Time: ${new Date().toLocaleString()}`);
  
  try {
    // 1. Check current state
    console.log("\n📊 PHASE 1: Current Cache State");
    console.log("-".repeat(30));
    const stats = await verifySyncBetweenCollections();
    console.log(`✅ svgMetadata (authoritative): ${stats.svgMetadataCount} files`);
    console.log(`✅ accountData cache: ${stats.accountDataCount} generated files`);
    console.log(`${stats.mismatches.length === 0 ? '✅' : '⚠️'} Sync mismatches: ${stats.mismatches.length}`);
    
    if (stats.mismatches.length > 0) {
      console.log("   Mismatch details:", stats.mismatches);
    }
    
    // 2. Test the lazy loading by fetching files
    console.log("\n🔄 PHASE 2: Testing Lazy Cache Loading");
    console.log("-".repeat(30));
    console.log("Calling uploadedSVGsInStorage() to trigger lazy cache...");
    const result = await uploadedSVGsInStorage(null, 8, true); // Use 8 to see more results
    
    console.log(`\n📋 FETCH RESULTS:`);
    console.log(`   Files returned: ${result.files.length}`);
    console.log(`   Has more pages: ${result.hasMore}`);
    
    // Show detailed file breakdown
    if (result.files.length > 0) {
      console.log(`\n📁 FILE BREAKDOWN:`);
      result.files.forEach((file, index) => {
        console.log(`   ${index + 1}. ${file.id}`);
        console.log(`      Type: ${file.type}`);
        console.log(`      Public: ${file.isPublic}`);
        console.log(`      Name: "${file.prompt || file.name || 'No name'}"`);
      });
    }
    
    // 3. Check state after lazy loading
    console.log("\n📊 PHASE 3: Cache State After Lazy Loading");
    console.log("-".repeat(30));
    const statsAfter = await verifySyncBetweenCollections();
    console.log(`✅ svgMetadata (authoritative): ${statsAfter.svgMetadataCount} files`);
    console.log(`✅ accountData cache: ${statsAfter.accountDataCount} generated files`);
    console.log(`${statsAfter.mismatches.length === 0 ? '✅' : '⚠️'} Sync mismatches: ${statsAfter.mismatches.length}`);
    
    // 4. Summary and conclusions
    const cacheImprovement = statsAfter.accountDataCount - stats.accountDataCount;
    console.log("\n🎯 FINAL SUMMARY");
    console.log("=" .repeat(50));
    console.log(`📈 Cache improvement: +${cacheImprovement} files automatically cached`);
    console.log(`📊 Total files in your account: ${statsAfter.svgMetadataCount}`);
    console.log(`💾 Cache coverage: ${Math.round((statsAfter.accountDataCount / statsAfter.svgMetadataCount) * 100)}%`);
    console.log(`🔄 Lazy cache ${cacheImprovement > 0 ? 'WORKED' : 'not needed'} - ${cacheImprovement > 0 ? 'added missing files!' : 'cache was already complete!'}`);
    
    // Privacy breakdown
    const generatedFiles = result.files.filter(f => f.type === "generated");
    const publicFiles = result.files.filter(f => f.isPublic === true);
    const privateFiles = result.files.filter(f => f.isPublic === false);
    
    console.log(`\n🔒 PRIVACY STATUS:`);
    console.log(`   Generated files: ${generatedFiles.length} (can be public/private)`);
    console.log(`   Public files: ${publicFiles.length}`);
    console.log(`   Private files: ${privateFiles.length}`);
    console.log(`   Default behavior: Generated files default to PUBLIC ✅`);
    
    console.log("\n✅ TEST COMPLETE! Your account caching is working correctly.");
    
    return {
      userId: user.uid,
      initial: stats,
      after: statsAfter,
      filesReturned: result.files.length,
      cacheImprovement,
      cacheEfficiency: Math.round((statsAfter.accountDataCount / statsAfter.svgMetadataCount) * 100),
      privacySummary: {
        generated: generatedFiles.length,
        public: publicFiles.length,
        private: privateFiles.length
      }
    };
  } catch (error) {
    console.error("❌ TEST FAILED:", error);
    console.error("   Error details:", error.message);
    throw error;
  }
};

// Temporary debugging function to check if a file exists in svgMetadata
export const debugCheckFileInSvgMetadata = async (filePath) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log("❌ No authenticated user");
      return;
    }

    console.log(`🔍 DEBUG: Checking if file exists in svgMetadata...`);
    console.log(`   User: ${user.uid}`);
    console.log(`   FilePath: ${filePath}`);

    const svgMetadataRef = collection(db, METADATA_COLLECTION);
    const q = query(
      svgMetadataRef,
      where("ownerUid", "==", user.uid),
      where("filePath", "==", filePath)
    );

    const querySnapshot = await getDocs(q);
    
    console.log(`📊 Result: Found ${querySnapshot.docs.length} matching documents`);
    
    if (querySnapshot.docs.length > 0) {
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log(`✅ Found: Doc ID ${doc.id}`);
        console.log(`   isPublic: ${data.isPublic}`);
        console.log(`   fileType: ${data.fileType}`);
        console.log(`   prompt: ${data.prompt}`);
        console.log(`   createdAt: ${data.createdAt}`);
      });
    } else {
      console.log(`❌ File NOT found in svgMetadata - this is a legacy file`);
    }
  } catch (error) {
    console.error("❌ Error checking file:", error);
  }
};

// Call this function from the browser console like:
// debugCheckFileInSvgMetadata("your_user_id/svg/filename.svg")

/**
 * Get user preferences from Firestore
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} - User preferences object
 */
export const getUserPreferences = async (userId) => {
  try {
    const db = getFirestore();
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      return data.preferences || {};
    }
    
    // Try searching by uid field (fallback for older accounts)
    const usersQuery = query(collection(db, "users"), where("uid", "==", userId));
    const querySnapshot = await getDocs(usersQuery);
    
    if (!querySnapshot.empty) {
      const data = querySnapshot.docs[0].data();
      return data.preferences || {};
    }
    
    return {};
  } catch (error) {
    console.error("Error getting user preferences:", error);
    return {};
  }
};

/**
 * Update user preferences in Firestore
 * @param {string} userId - The user's UID
 * @param {Object} preferences - Preferences to update
 * @returns {Promise<boolean>} - Success status
 */
export const updateUserPreferences = async (userId, preferences) => {
  try {
    const db = getFirestore();
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      await updateDoc(userDocRef, {
        preferences: {
          ...(userDoc.data().preferences || {}),
          ...preferences,
        },
        updatedAt: serverTimestamp(),
      });
      console.log("✅ User preferences updated for:", userId);
      return true;
    }
    
    // Try searching by uid field (fallback for older accounts)
    const usersQuery = query(collection(db, "users"), where("uid", "==", userId));
    const querySnapshot = await getDocs(usersQuery);
    
    if (!querySnapshot.empty) {
      const docRef = querySnapshot.docs[0].ref;
      const existingPrefs = querySnapshot.docs[0].data().preferences || {};
      await updateDoc(docRef, {
        preferences: {
          ...existingPrefs,
          ...preferences,
        },
        updatedAt: serverTimestamp(),
      });
      console.log("✅ User preferences updated for:", userId);
      return true;
    }
    
    console.error("User document not found for:", userId);
    return false;
  } catch (error) {
    console.error("Error updating user preferences:", error);
    return false;
  }
};
